import { useState, useEffect, useCallback, useRef } from 'react';
import AdLibraryService from 'services/AdLibraryService';
import VideoCreativeService from 'services/VideoCreativeService';
import { ScrapedAd, AnalysisPrompt, LlmAnalysis, CIAdDetail } from './types';
import { shouldPoll } from 'shared/pollingGate';

const adService = new AdLibraryService();
const ciService = new VideoCreativeService();

export default function useAdPipeline(adId: string | null, onRefresh?: () => void, onAdUpdated?: (ad: ScrapedAd) => void) {
  const [ad, setAd] = useState<ScrapedAd | null>(null);
  const [loading, setLoading] = useState(false);
  const [transcript, setTranscript] = useState<string | null>(null);
  // Per-Whisper-segment timestamps, fetched alongside the displayed transcript.
  // Used in the dialog to align each displayed (Haiku-reformatted) line back
  // to wall-clock seconds, so each line can show a timestamp.
  const [transcriptSegments, setTranscriptSegments] = useState<Array<{ start: number; text: string }> | null>(null);
  const [analyses, setAnalyses] = useState<LlmAnalysis[]>([]);
  const [prompts, setPrompts] = useState<AnalysisPrompt[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [ciDetail, setCiDetail] = useState<CIAdDetail | null>(null);
  const [ciLoading, setCiLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [pipelineRunning, setPipelineRunning] = useState(false);

  // Bug 1 fix: track which adId CI detail was fetched for, instead of !ciDetail && !ciLoading guard
  const [ciFetchedForAdId, setCiFetchedForAdId] = useState<string | null>(null);

  // Bug 9 fix: ref to avoid stale transcript closure in pipeline poll
  const transcriptFetchedRef = useRef(false);

  // Ref to guard against stale responses in loadCiDetail
  const adIdRef = useRef(adId);
  adIdRef.current = adId;

  // Error state for surfacing trigger failures
  const [error, setError] = useState<string | null>(null);

  // ── Effect: Initial data load ──
  // Bug 2: resets all process flags on adId change
  // Bug 3: isCancelled prevents stale responses from overwriting state
  // Bug 10: .catch/.finally ensure loading is always cleared
  useEffect(() => {
    if (!adId) {
      setAd(null);
      setTranscript(null);
      setTranscriptSegments(null);
      setAnalyses([]);
      setPrompts([]);
      setCiDetail(null);
      setAnalyzing(false);
      setExtracting(false);
      setPipelineRunning(false);
      setCiFetchedForAdId(null);
      setCiLoading(false);
      transcriptFetchedRef.current = false;
      return;
    }

    // Bug 2: reset process flags for new ad
    setAnalyzing(false);
    setExtracting(false);
    setPipelineRunning(false);
    setCiFetchedForAdId(null);
    setCiDetail(null);
    setCiLoading(false);
    setLoading(true);
    transcriptFetchedRef.current = false;

    let isCancelled = false;

    Promise.all([
      adService.getAd(adId),
      adService.listAnalyses({ adId }),
      adService.listPrompts(),
    ]).then(([adData, analysisData, promptData]) => {
      if (isCancelled) return;
      setAd(adData);
      if (analysisData) {
        setAnalyses(analysisData.analyses);
        const hasInProgress = analysisData.analyses.some(a => a.status === 'PENDING' || a.status === 'PROCESSING');
        if (hasInProgress) setAnalyzing(true);
      }
      if (promptData) {
        setPrompts(promptData.prompts);
      }
      if (adData?.transcriptUrl) {
        fetch(adData.transcriptUrl)
          .then(r => r.text())
          .then(t => { if (!isCancelled) { setTranscript(t); transcriptFetchedRef.current = true; } })
          .catch(() => { if (!isCancelled) setTranscript(null); });
      } else {
        setTranscript(null);
      }
      // Whisper verbose-JSON for per-line timestamp alignment. Best-effort —
      // failure is non-fatal; the dialog just omits timestamps in that case.
      if (adData?.transcriptJsonUrl) {
        fetch(adData.transcriptJsonUrl)
          .then(r => r.json())
          .then(j => {
            if (isCancelled) return;
            const segs = Array.isArray(j?.segments)
              ? j.segments.filter((s: any) => typeof s?.start === 'number' && typeof s?.text === 'string')
                .map((s: any) => ({ start: s.start as number, text: s.text as string }))
              : null;
            setTranscriptSegments(segs);
          })
          .catch(() => { if (!isCancelled) setTranscriptSegments(null); });
      } else {
        setTranscriptSegments(null);
      }
    }).catch(err => {
      if (isCancelled) return;
      console.error('Failed to load ad detail', err);
    }).finally(() => {
      if (!isCancelled) setLoading(false);
    });

    return () => { isCancelled = true; };
  }, [adId]);

  // ── Effect: Propagate ad changes to parent ──
  useEffect(() => {
    if (ad) onAdUpdated?.(ad);
  }, [ad]);

  // ── Effect: Analysis polling (skips when pipeline is running — pipeline poll handles it) ──
  useEffect(() => {
    if (!analyzing || !adId || pipelineRunning) return;
    let isCancelled = false;
    let intervalId: ReturnType<typeof setInterval>;

    const poll = async () => {
      // Gate: skip while the tab is hidden or the user has been idle >5 min, so a
      // dashboard left open doesn't keep hitting Snowflake. Resumes on activity.
      if (!shouldPoll()) return;
      const data = await adService.listAnalyses({ adId });
      if (isCancelled) return;
      if (data) {
        setAnalyses(data.analyses);
        const stillProcessing = data.analyses.some(a => a.status === 'PENDING' || a.status === 'PROCESSING');
        if (!stillProcessing) setAnalyzing(false);
      }
    };

    const firstTimeout = setTimeout(() => {
      if (isCancelled) return;
      poll();
      intervalId = setInterval(poll, 15000);
    }, 3000);

    return () => { isCancelled = true; clearTimeout(firstTimeout); clearInterval(intervalId); };
  }, [analyzing, adId, pipelineRunning]);

  // ── Effect: Extraction polling (standalone — skips when pipeline is running) ──
  // Bug 7: guard prevents dual polling when pipelineRunning is true
  useEffect(() => {
    if (!extracting || !adId || pipelineRunning) return;
    let isCancelled = false;
    let intervalId: ReturnType<typeof setInterval>;
    let seenInProgress = false;

    const poll = async () => {
      if (!shouldPoll()) return;
      const adData = await adService.getAd(adId);
      if (isCancelled) return;
      const status = adData?.extractionStatus;
      if (status === 'PENDING' || status === 'PROCESSING') seenInProgress = true;
      const isTerminal = status === 'DONE' || (status === 'FAILED' && seenInProgress);
      if (isTerminal) {
        setAd(adData);
        const fullDetail = await ciService.getAd(adId);
        if (isCancelled) return;
        setCiDetail(fullDetail);
        setCiFetchedForAdId(adId);
        setExtracting(false);
      }
    };

    const firstCheck = setTimeout(() => {
      if (isCancelled) return;
      poll();
      intervalId = setInterval(poll, 15000);
    }, 5000);

    return () => { isCancelled = true; clearTimeout(firstCheck); clearInterval(intervalId); };
  }, [extracting, adId, pipelineRunning]);

  // ── Effect: Pipeline polling ──
  // Polls lightweight status every 10s. On completion, does a full parallel refresh of everything.
  useEffect(() => {
    if (!pipelineRunning || !adId) return;
    let isCancelled = false;
    let intervalId: ReturnType<typeof setInterval>;
    let seenExtractionInProgress = false;

    // Track which steps are done so we stop querying for them
    let transcriptionDone = false;
    let analysesFinished = false;

    const poll = async () => {
      if (!shouldPoll()) return;
      // Only fetch what's still in progress:
      // - getAd: needed for transcription status OR extraction status (skip during analysis-only phase)
      // - listAnalyses: needed until all analyses are terminal
      const needsAdFetch = !transcriptionDone || analysesFinished;
      const adData = needsAdFetch ? await adService.getAd(adId) : null;
      const analysisData = !analysesFinished
        ? await adService.listAnalyses({ adId })
        : null;
      if (isCancelled) return;

      const currentAd = adData;
      const exStatus = currentAd?.extractionStatus;

      if (currentAd) {
        setAd(currentAd);
        if (exStatus === 'PENDING' || exStatus === 'PROCESSING') {
          seenExtractionInProgress = true;
          setExtracting(true);
        }
        if (currentAd.transcriptUrl && !transcriptFetchedRef.current) {
          fetch(currentAd.transcriptUrl)
            .then(r => r.text())
            .then(t => { if (!isCancelled) { setTranscript(t); transcriptFetchedRef.current = true; } })
            .catch(() => {});
        }
        if (currentAd.transcriptionStatus === 'DONE') transcriptionDone = true;
      }

      if (analysisData) {
        const currentAnalyses = analysisData.analyses;
        setAnalyses(currentAnalyses);
        const hasInProgress = currentAnalyses.some(a => a.status === 'PENDING' || a.status === 'PROCESSING');
        setAnalyzing(hasInProgress);
        if (currentAnalyses.length > 0 && !hasInProgress) analysesFinished = true;
      }

      // Check if pipeline is fully complete
      const extractionTerminal = exStatus === 'DONE' || (exStatus === 'FAILED' && seenExtractionInProgress);

      if (transcriptionDone && analysesFinished && extractionTerminal) {
        const fullDetail = await ciService.getAd(adId);
        if (isCancelled) return;
        setCiDetail(fullDetail);
        setCiFetchedForAdId(adId);
        setExtracting(false);
        setAnalyzing(false);
        setPipelineRunning(false);
        onRefresh?.();
      }
    };

    const firstCheck = setTimeout(() => {
      if (isCancelled) return;
      poll();
      intervalId = setInterval(poll, 10000);
    }, 3000);

    return () => { isCancelled = true; clearTimeout(firstCheck); clearInterval(intervalId); };
  }, [pipelineRunning, adId]);

  // ── Action: Load CI detail (lazy, on tab switch) ──
  const loadCiDetail = useCallback(({ refresh = false }: { refresh?: boolean } = {}) => {
    if (!adId) return;
    if (!refresh && (ciFetchedForAdId === adId || ciLoading)) return;
    setCiLoading(true);
    const requestAdId = adId;

    ciService.getAd(adId, { refresh }).then(data => {
      if (adIdRef.current !== requestAdId) return; // stale response
      setCiDetail(data);
      setCiFetchedForAdId(requestAdId);
      setCiLoading(false);
      const status = data?.extractionStatus;
      if (status === 'PENDING' || status === 'PROCESSING') {
        setExtracting(true);
      }
    }).catch(() => {
      if (adIdRef.current !== requestAdId) return;
      setCiFetchedForAdId(requestAdId);
      setCiLoading(false);
    });
  }, [adId, ciFetchedForAdId, ciLoading]);

  // ── Action: Transcribe ──
  const transcribe = useCallback(async () => {
    if (!adId) return;
    // Optimistically show "in progress" immediately
    setAd(prev => prev ? { ...prev, transcriptionStatus: 'QUEUED' } : prev);
    const result = await adService.transcribeAd(adId);
    if (result === null) {
      setAd(prev => prev ? { ...prev, transcriptionStatus: null } : prev);
      setError('Failed to trigger transcription');
      return;
    }
    const freshAd = await adService.getAd(adId);
    if (freshAd) setAd(freshAd);
    onRefresh?.();
  }, [adId, onRefresh]);

  // ── Action: Delete Analysis ──
  const [deletingAnalysisId, setDeletingAnalysisId] = useState<string | null>(null);
  const deleteAnalysis = useCallback(async (analysisId: string) => {
    setDeletingAnalysisId(analysisId);
    const result = await adService.deleteAnalysis(analysisId);
    setDeletingAnalysisId(null);
    if (result === null) { setError('Failed to delete analysis'); return; }
    setAnalyses(prev => prev.filter(a => a.analysisId !== analysisId));
  }, []);

  // ── Action: Analyze ──
  const analyze = useCallback(async (promptIds: string[], model: string) => {
    if (!adId || promptIds.length === 0) return;
    setAnalyzing(true);
    // Optimistically add PENDING analyses for each selected prompt
    const optimisticAnalyses: LlmAnalysis[] = promptIds.map(promptId => ({
      analysisId: `pending-${promptId}-${Date.now()}`,
      adId,
      promptId,
      transcriptionJobId: null,
      model,
      inputText: null,
      promptTextUsed: null,
      resultText: null,
      resultJson: null,
      tokensIn: null,
      tokensOut: null,
      status: 'PENDING',
      errorMessage: null,
      createdAt: new Date().toISOString(),
      finishedAt: null,
    }));
    setAnalyses(prev => [...optimisticAnalyses, ...prev]);
    for (const promptId of promptIds) {
      await adService.analyzeAd(adId, promptId, model);
    }
    // Polling effect picks up real status and replaces optimistic entries
  }, [adId]);

  // ── Action: Extract ──
  const extract = useCallback(async (analysisIds: string[], model: string) => {
    if (!adId) return;
    setExtracting(true);
    const result = await ciService.triggerExtraction(adId, analysisIds.length > 0 ? analysisIds : undefined, model);
    if (result === null) {
      setExtracting(false);
      setError('Failed to trigger extraction');
      return;
    }
    // Refresh ad so extractionStatus is PENDING (clears stale FAILED before polling starts)
    const freshAd = await adService.getAd(adId);
    if (freshAd) setAd(freshAd);
  }, [adId]);

  // ── Action: Run Pipeline ──
  const runPipeline = useCallback(async (promptIds: string[], analysisModel: string, extractionModel: string) => {
    if (!adId) return;
    setPipelineRunning(true);
    const result = await adService.runPipeline(adId, promptIds, analysisModel, extractionModel);
    if (result === null) {
      setPipelineRunning(false);
      setError('Failed to start pipeline');
      return;
    }
    if (result?.status === 'complete') {
      setPipelineRunning(false);
      return;
    }
    // 'already_running' or normal start — refresh ad so polling sees current status
    const freshAd = await adService.getAd(adId);
    if (freshAd) setAd(freshAd);
  }, [adId]);

  const clearError = useCallback(() => setError(null), []);

  return {
    ad,
    transcript,
    transcriptSegments,
    analyses,
    prompts,
    ciDetail,
    loading,
    ciLoading,
    analyzing,
    extracting,
    pipelineRunning,
    error,
    clearError,
    deletingAnalysisId,
    loadCiDetail,
    transcribe,
    analyze,
    deleteAnalysis,
    extract,
    runPipeline,
  };
}
